import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwn511Api, ApproveCandidate, ApproveItem } from '../../services/pwn511Api';
import { BatchAcceptanceBridge, pwn511f1AcceptanceManifest } from '../../features/acceptance';
import './PWN511Form.css';

// ---- Item state with selection and per-item opinion ----
interface ItemState {
  item: ApproveCandidate;
  selected: boolean;
  opinion: string;
}

// ---- Component ----
const PWN511Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwn511Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
        opinion: item.fn110_8111 || '',
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的客訴單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fn110_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Update per-item opinion ----
  const handleOpinionChange = useCallback((no: string, value: string) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fn110_01 === no ? { ...s, opinion: value } : s
      )
    );
  }, []);

  // ---- Execute approval ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要核准的客訴單');
      return;
    }
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆客訴單？此操作不可復原。`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedItems: ApproveItem[] = items
          .filter((s) => s.selected)
          .map((s) => ({ no: s.item.fn110_01, opinion: s.opinion }));
        setExecuting(true);
        try {
          const res = await pwn511Api.approve(selectedItems);
          if (res.failed === 0) {
            message.success(res.message || `核准完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '核准執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fn110_01, e.target.checked)}
        />
      ),
    },
    {
      title: '客訴單號',
      dataIndex: ['item', 'fn110_01'],
      key: 'fn110_01',
      width: 110,
    },
    {
      title: '客訴日期',
      dataIndex: ['item', 'fn110_11'],
      key: 'fn110_11',
      width: 110,
    },
    {
      title: '廠商總額',
      dataIndex: ['item', 'fn110_17'],
      key: 'fn110_17',
      width: 110,
      align: 'right',
    },
    {
      title: '賠償金額',
      dataIndex: ['item', 'fn110_pay'],
      key: 'fn110_pay',
      width: 110,
      align: 'right',
    },
    {
      title: '採購組長意見',
      key: 'opinion',
      width: 240,
      render: (_: unknown, record: ItemState) => (
        <Input.TextArea
          className="opinion-cell"
          value={record.opinion}
          onChange={(e) => handleOpinionChange(record.item.fn110_01, e.target.value)}
          rows={1}
          autoSize={{ minRows: 1, maxRows: 3 }}
          placeholder="請輸入意見說明"
          maxLength={200}
        />
      ),
    },
  ];

  // ---- Render ----
  return (
    <div className="pwn511-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                客訴管理
              </a>
              <span>/</span>
              <span className="current">客訴單-採購組長核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>客訴單-採購組長核准</h1>
            <span className="badge">PWN511</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>客訴單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fn110_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入客訴單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            核准 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwn511f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((row) => row.selected).map((row) => row.item.fn110_01)}
          keyOf={(row) => row.item.fn110_01}
          queryExact={(key) => pwn511Api.queryCandidates(key, key)}
          lastApiResponse={items}
          loading={loading || executing}
          onAutoFill={() => items[0] && setItems((current) => current.map((row, index) => ({ ...row, selected: index === 0 })))}
          autoFillDisabled={items.length === 0}
          screenDraft={{ startNo, endNo }}
        />
      </div>
    </div>
  );
};

export default PWN511Form;
